import React, { createContext, forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import { translateSource } from './sourceCopy';
import { formattingLocale } from './formattingLocale';

const attributeCopy = Symbol('authored-interface-copy');
export function sourceAttribute(source, pattern) { return { [attributeCopy]: true, source, pattern }; }

const SourceCopyContext = createContext({ lang: 'fr', translate: value => value });
let cachedPack;
let loadingPack;
const loadPack = () => {
  if (!loadingPack) {
    loadingPack = import(/* webpackChunkName: "eternua-english-copy" */ './generated/siteEnglish.json')
      .then(module => { cachedPack = module.default || module; return cachedPack; })
      .catch(error => { loadingPack = undefined; throw error; });
  }
  return loadingPack;
};

export function SourceCopyProvider({ lang, children, catalog }) {
  const [pack, setPack] = useState(cachedPack);
  const activePack = catalog || pack;
  useEffect(() => {
    if (lang !== 'en' || activePack) return undefined;
    let active = true;
    loadPack().then(next => { if (active) setPack(next); }).catch(() => {});
    return () => { active = false; };
  }, [lang, activePack]);
  const value = useMemo(() => ({ lang, translate: (source, pattern) => translateSource(source, lang, activePack, pattern) }), [lang, activePack]);
  return <SourceCopyContext.Provider value={value}>{children}</SourceCopyContext.Provider>;
}

export function LocalizedText({ source, pattern, marked = true }) {
  const { translate } = useContext(SourceCopyContext);
  return marked ? translate(source, pattern) : source;
}

// No additional DOM node. Keep events, refs, styles and original option values.
export const LocalizedElement = forwardRef(function LocalizedElement({ elementType: Element, copyAttributes = [], copyChildren = false, ...props }, ref) {
  const { translate } = useContext(SourceCopyContext);
  const translatedProps = { ...props };
  for (const name of copyAttributes) {
    const value = props[name];
    if (value?.[attributeCopy]) translatedProps[name] = translate(value.source, value.pattern);
  }
  if (copyChildren) {
    if (Element === 'option' && props.value === undefined && typeof props.children === 'string') translatedProps.value = props.children;
    translatedProps.children = React.Children.map(props.children, child => typeof child === 'string' ? translate(child) : child);
  }
  if (ref) translatedProps.ref = ref;
  return React.createElement(Element, translatedProps);
});

export function translateLoadedSource(source, language) {
  return translateSource(source, language, cachedPack);
}

export function useSourceLanguage() {
  return useContext(SourceCopyContext).lang;
}

export function displayAttribute(source, pattern) { return translateSource(source, formattingLocale() === 'en-GB' ? 'en' : 'fr', cachedPack, pattern); }

// For explicitly identified repository-authored editorial content only.
export function useSourceTranslation() { return useContext(SourceCopyContext); }
